import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh, setUserAndToken } = useAuth();
  const processed = useRef(false);
  const [pending, setPending] = useState(null); // {email, name, message}

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        // Flusso 2: nuovo utente → account in attesa, blocco schermata
        if (data?.status === "pending") {
          setPending({
            email: data.email,
            name: data.name,
            message: data.message || "Il tuo account è in fase di approvazione.",
          });
          // Pulisci l'hash OAuth per non ri-processare al refresh
          try { window.history.replaceState(null, "", "/login"); } catch {}
          return;
        }
        // Flusso 1: utente esistente → login diretto
        if (data?.session_token) {
          try { localStorage.setItem("access_token", data.session_token); } catch {}
        }
        if (data?.user && setUserAndToken) {
          setUserAndToken(data.user, data.session_token);
        } else {
          await refresh();
        }
        toast.success(`Benvenuto, ${data?.user?.name || data?.user?.email || ""}!`);
        nav("/", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Accesso Google fallito";
        toast.error(typeof msg === "string" ? msg : "Accesso Google fallito");
        nav("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (pending) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
        <div className="max-w-md w-full rounded-xl border border-primary/30 bg-primary/5 p-8 text-center space-y-4" data-testid="pending-screen">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center">
            <ShieldCheck className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-extrabold">Account in fase di approvazione</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Ciao <strong>{pending.name}</strong>, il tuo accesso Google con <strong>{pending.email}</strong> è stato registrato.
              L'Amministratore ti attiverà a breve: riceverai accesso completo appena il tuo profilo verrà approvato.
            </p>
          </div>
          <div className="rounded-lg bg-background/70 border p-3 text-xs text-left">
            <div className="flex items-center gap-2 font-bold text-foreground mb-1">
              <Mail className="h-3.5 w-3.5" /> Prossimi passi
            </div>
            <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
              <li>Ti invitiamo a contattare l'Amministratore per velocizzare l'approvazione.</li>
              <li>Al prossimo accesso con Google l'app sbloccherà le funzioni operative.</li>
            </ul>
          </div>
          <Button data-testid="pending-back-login" variant="outline" className="w-full" onClick={() => nav("/login", { replace: true })}>
            Torna al login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
      <div className="text-sm text-muted-foreground">Accesso in corso…</div>
    </div>
  );
}
